/**
 * AuthCallback — Phase B.5
 * Runs when the URL has #session_id=… in the fragment (set by Emergent
 * Auth after the user's Google sign-in). Exchanges that session_id for
 * an httpOnly cookie via POST /api/auth/session and then hands off to
 * the role's landing page.
 */
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { authExchangeSession } from "../lib/api";
import { ROLE_LANDING, useAuth } from "../lib/AuthProvider";
import { toast } from "sonner";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = React.useState(null);

  useEffect(() => {
    // useRef + synchronous set to prevent StrictMode double-fire.
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const user = await authExchangeSession(sessionId);
        setUser(user);
        // Wipe the fragment so refreshing doesn't re-exchange.
        window.history.replaceState(null, "", window.location.pathname);
        const landing = ROLE_LANDING[user.role] || "/dashboard";
        toast.success(`Welcome back, ${user.name || user.email}`);
        navigate(landing, { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "Login failed";
        setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      }
    })();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-100 p-6">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-semibold">Can't sign you in</h2>
          <p className="text-sm text-zinc-400 leading-relaxed">{error}</p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="text-emerald-400 hover:text-emerald-300 text-sm font-medium underline-offset-2 hover:underline"
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-100">
      <div className="flex items-center gap-3 text-zinc-300">
        <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
        Signing you in…
      </div>
    </div>
  );
}
